import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import axios from 'axios';
import { getUrl } from '../../app/api/agent';
import { getToken } from '../../app/services/AuthService';

const MO_SCORE_OPTIONS = [0, 1, 2, 3, 4, 5, 6, 7];

const SecondaryPersonCard = ({ index, data, onChange, onRemove }) => {
  const [ntaLookup, setNtaLookup] = useState({
    loading: false,
    found: null,
    score: null,
  });

  const handleFieldChange = (field, value) => {
    onChange(index, { ...data, [field]: value });
  };

  const handleEmailBlur = async () => {
    const email = data.email?.trim();
    if (!email) return;

    setNtaLookup({ loading: true, found: null, score: null });

    try {
      const token = getToken();
      const response = await axios({
        url: `${getUrl()}/lookup-nta?email=${encodeURIComponent(
          email
        )}&token=${encodeURIComponent(token)}`,
        method: 'get',
      });

      const { ntaScore, found } = response.data;
      setNtaLookup({ loading: false, found, score: ntaScore });
      onChange(index, { ...data, ntaScore: ntaScore });
    } catch (error) {
      console.error('Error looking up NTA score:', error);
      setNtaLookup({ loading: false, found: false, score: null });
      onChange(index, { ...data, ntaScore: null });
    }
  };

  return (
    <Box
      sx={{
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        p: 2.5,
        position: 'relative',
        backgroundColor: '#f8fafc',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              backgroundColor: '#326a87',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem' }}
            >
              {index + 2}
            </Typography>
          </Box>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, color: '#3e4959' }}
          >
            Person {index + 2}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={() => onRemove(index)}
          sx={{
            color: '#94a3b8',
            '&:hover': { color: '#ef4444', backgroundColor: '#fef2f2' },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <TextField
        fullWidth
        required
        label="Email"
        type="email"
        value={data.email || ''}
        onChange={(e) => handleFieldChange('email', e.target.value)}
        onBlur={handleEmailBlur}
        sx={{
          mb: 2,
          '& .MuiOutlinedInput-root': { borderRadius: '10px' },
        }}
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={data.isMinor || false}
            onChange={(e) => handleFieldChange('isMinor', e.target.checked)}
          />
        }
        label="This person is a minor"
        sx={{ mb: 1, display: 'block' }}
      />

      <Box sx={{ mb: ntaLookup.found != null || ntaLookup.loading ? 1.5 : 0 }}>
        {ntaLookup.loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={14} />
            <Typography variant="body2" color="text.secondary">
              Looking up NTA score...
            </Typography>
          </Box>
        ) : ntaLookup.found === true ? (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              backgroundColor: '#f0f7ff',
              border: '1px solid #bae6fd',
              borderRadius: '6px',
              px: 1.5,
              py: 0.5,
            }}
          >
            <Typography variant="body2" sx={{ color: '#326a87' }}>
              NTA Score:
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, color: '#3e4959' }}
            >
              {ntaLookup.score}
            </Typography>
          </Box>
        ) : ntaLookup.found === false ? (
          <Typography variant="body2" color="text.secondary">
            NTA Score: Not available.{' '}
            <Link href="/nta" target="_blank" rel="noopener">
              They can take the assessment here
            </Link>
          </Typography>
        ) : null}
      </Box>

      <FormControl fullWidth required>
        <InputLabel>Cellular Inflammation Test Score</InputLabel>
        <Select
          value={data.moScore || ''}
          onChange={(e) => handleFieldChange('moScore', e.target.value)}
          label="Cellular Inflammation Test Score"
          sx={{ borderRadius: '10px' }}
        >
          {MO_SCORE_OPTIONS.map((val) => (
            <MenuItem key={val} value={val}>
              {val}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default SecondaryPersonCard;
